import { useGetAiTranslationUsage } from '@agos/api-client/ai'
import { T, formatCount, tt } from '@agos/shared'
import { AlertTriangle } from 'lucide-react'
import { useMemo } from 'react'

import {
  emptyUsageNote,
  formatCost,
  hasUsage,
  translationUsageOf,
} from './translation-usage'

/**
 * What the translations have cost — §9.255, FB-415.
 *
 * `GET /ai/translation-usage` was served and read by nothing, while the two buttons below this panel
 * each start **hundreds of billable calls** on the owner's Claude key. This page's own header has
 * always promised *"a čo to stojí"*.
 *
 * Read-only: nothing here spends, it reports what already has.
 */
export function TranslationUsagePanel() {
  const { data } = useGetAiTranslationUsage()
  const usage = useMemo(() => translationUsageOf(data), [data])

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-3" data-usage>
      <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
        <T>Čo preklad stál</T>
      </h2>

      {!hasUsage(usage) ? (
        <p className="text-xs text-muted-foreground" data-usage-empty>
          {emptyUsageNote()}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Tile label={tt('Náklady')} value={formatCost(usage.totals.costEur)} testId="cost" />
            <Tile
              label={tt('Volaní')}
              value={formatCount(usage.totals.totalCalls)}
              // The count is what makes a six-decimal figure legible at a glance.
              sub={`${formatCount(usage.totals.okCalls)} ${tt('úspešných')}`}
              testId="calls"
            />
            <Tile
              label={tt('Chýb')}
              value={formatCount(usage.totals.errorCalls)}
              testId="errors"
            />
            <Tile
              label={tt('Tokenov')}
              value={formatCount(usage.totals.inputTokens + usage.totals.outputTokens)}
              testId="tokens"
            />
          </div>

          {usage.byLanguage.length > 0 && (
            <div className="divide-y divide-border" data-usage-languages={usage.byLanguage.length}>
              {usage.byLanguage.map((language) => (
                <div
                  key={language.lang}
                  className="flex items-center justify-between gap-3 py-1.5 text-xs"
                  data-usage-language={language.lang}
                >
                  <span className="font-medium uppercase">{language.lang}</span>
                  <span className="text-muted-foreground">
                    {formatCount(language.calls)} <T>volaní</T>
                  </span>
                  <span className="tabular-nums font-semibold">{formatCost(language.costEur)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Beside the cost, not under it: a run that half-failed spends the same money and
              translates half the menu. */}
          {usage.recentErrors.length > 0 && (
            <div className="space-y-1.5" data-usage-errors={usage.recentErrors.length}>
              {usage.recentErrors.map((error, index) => (
                <p
                  key={`${error.createdAt ?? ''}-${index}`}
                  className="flex items-start gap-1.5 text-[11px] text-amber-500"
                  data-usage-error
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>
                    {error.targetLang !== null && <strong className="uppercase">{error.targetLang}</strong>}{' '}
                    {error.errorMessage ?? error.status}
                    {error.sourceExcerpt !== null && ` - ${error.sourceExcerpt}`}
                  </span>
                </p>
              ))}
            </div>
          )}

          {usage.totals.lastCallAt !== null && (
            <p className="text-[11px] text-muted-foreground" data-usage-last>
              <T>Naposledy</T> {usage.totals.lastCallAt.slice(0, 16)}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function Tile({
  label,
  value,
  sub,
  testId,
}: {
  label: string
  value: string
  sub?: string
  testId: string
}) {
  return (
    <div className="rounded-xl border border-border p-3" data-usage-tile={testId}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
      <p className="text-lg font-bold tabular-nums">{value}</p>
      {sub !== undefined && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  )
}
